import { ApolloError, gql, useQuery } from '@apollo/client';
import { getTokenData, logout } from '../services/auth';
import { UserMode } from './useApolloClient';
import { useMemo } from 'react';
import * as Sentry from '@sentry/react';

const GET_USER = gql`
  query GetMe {
    me {
      id
      type
      name
      timezone
      user {
        profileId
        type
        email
      }
    }

    session {
      companyProfile {
        id
        name
        type
        backflowTester {
          created
        }
        waterSupplier {
          profileId
        }
      }
    }
  }
`;

type ProfileData = {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  name: string;
  timezone?: string;
  user: {
    type: 'ADMIN' | 'NORMAL';
    email: string;
  };
};

type SessionData = {
  companyProfile?: {
    id: string;
    name: string;
    type: 'INDIVIDUAL' | 'COMPANY';
    backflowTester?: {
      created: number;
    };
    waterSupplier?: {
      profileId: string;
    };
  };
};

type UserData = {
  me: ProfileData;
  session: SessionData;
};

type PendingSessionInfo = {
  loading: true;
  isLoggedIn: undefined;
  isAdmin?: boolean;
  isCompany?: boolean;
  isWaterSupplier?: boolean;
  isBackflowTester: undefined;
  modes?: undefined;
  me?: ProfileData;
  session?: SessionData;
  error: undefined;
};

type LoggedOutSessionInfo = {
  loading: false;
  isLoggedIn: false;
  isAdmin: false;
  isCompany: false;
  isWaterSupplier: false;
  isBackflowTester: false;
  modes: [];
  me: undefined;
  session: undefined;
  error?: ApolloError;
};

type LoggedInSessionInfo = {
  loading: false;
  isLoggedIn: true;
  isAdmin: boolean;
  isCompany: boolean;
  isWaterSupplier: boolean;
  isBackflowTester: boolean;
  modes: UserMode[];
  me: ProfileData;
  session: SessionData;
  error?: ApolloError;
};

type SessionInfo =
  | PendingSessionInfo
  | LoggedOutSessionInfo
  | LoggedInSessionInfo;

export default (): SessionInfo => {
  const { data, error, loading } = useQuery<UserData>(GET_USER, {
    onCompleted: (data) => {
      Sentry.setUser({
        fullName: data.me.name,
        email: data.me.user.email,
        // username: data.me.user.email,
        id: data.me.id,
      });
    },
  });
  const sessionContext = useMemo(() => getTokenData(), []);

  if (loading) {
    return {
      loading: true,
      isLoggedIn: undefined,
      isAdmin: sessionContext?.modes.includes('ADMIN'),
      isCompany: sessionContext?.modes.includes('COMPANY'),
      isWaterSupplier: sessionContext?.modes.includes('WATER_SUPPLIER'),
      isBackflowTester: undefined,
      modes: undefined,
      me: sessionContext?.profile
        ? {
            ...sessionContext?.profile,
            id: sessionContext?.profile.id + '',
            type:
              sessionContext?.profile.id ===
              sessionContext?.session.companyProfileId
                ? 'COMPANY'
                : 'INDIVIDUAL',
            user: sessionContext?.user,
          }
        : undefined,
      session: sessionContext?.session
        ? {
            companyProfile: {
              id: sessionContext?.session.companyProfileId + '',
              name: '',
              type: 'COMPANY',
              waterSupplier: sessionContext?.modes.includes('WATER_SUPPLIER')
                ? {
                    profileId: sessionContext?.session.companyProfileId + '',
                  }
                : undefined,
            },
          }
        : undefined,
      error: undefined,
    };
  }

  if (error) {
    logout();
  }

  if (!data || error) {
    return {
      loading: false,
      isLoggedIn: false,
      isAdmin: false,
      isCompany: false,
      isWaterSupplier: false,
      isBackflowTester: false,
      modes: [],
      me: undefined,
      session: undefined,
      error,
    };
  }

  const { me, session } = data;

  const isCompany =
    !!session.companyProfile && session.companyProfile.type === 'COMPANY';
  const isWaterSupplier = isCompany && !!session.companyProfile?.waterSupplier;
  const isBackflowTester =
    isCompany && !!session.companyProfile?.backflowTester;
  const isAdmin = me.user.type === 'ADMIN';
  const modes: UserMode[] = [];
  if (isAdmin) {
    modes.push('admin');
  }
  if (isWaterSupplier) {
    modes.push('water-supplier');
  }
  if (isCompany && isBackflowTester) {
    modes.push('company');
  }

  return {
    loading: false,
    isLoggedIn: true,
    isAdmin,
    isCompany,
    isWaterSupplier,
    isBackflowTester,
    modes,
    me,
    session,
    error,
  };
};
